/* eslint-disable @typescript-eslint/no-explicit-any */
import { RcFile } from 'antd/lib/upload';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { auth } from '../state/firebase';
import { DEV_FLAG_KEYS } from '../state/setOnlyDevelopers';
import { fetchEventSource } from '@microsoft/fetch-event-source';

let cancelTokenSource: CancelTokenSource;

const axiosApiInstance = axios.create();

const applyDevFlagHeaders = (headers: Record<string, string>): void => {
  DEV_FLAG_KEYS.forEach(key => {
    if (localStorage.getItem(key) === 'true') headers[key] = 'true';
  });
};

axiosApiInstance.interceptors.request.use(
  async config => {
    if (config.headers) applyDevFlagHeaders(config.headers as Record<string, string>);
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

export default async function createApiInstance(accessToken: string, baseUrl: string) {
  axiosApiInstance.interceptors.request.use(
    async config => {
      const context = '';
      config.baseURL = `${baseUrl}${context}`;
      return config;
    },
    error => {
      Promise.reject(error);
    }
  );
}

export function handleRequestErrors(error: AxiosError) {
  const { response } = error as AxiosError;
  const statusError = response?.status;
  if (statusError === 403) window.open('/unauthorized', '_self');
}

export const getToken = async (): Promise<string> => {
  const user = auth.currentUser;
  let token = '';
  if (user) token = await user.getIdToken();
  return `Bearer ${token}`;
};

export async function apiGet<T, U>(url: string, options?: T): Promise<U> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data } = await axiosApiInstance.get<U>(url, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiGetWithCancelation<T, U>(url: string, options?: T): Promise<U> {
  if (cancelTokenSource) {
    cancelTokenSource.cancel('Solicitud cancelada');
  }
  cancelTokenSource = axios.CancelToken.source();
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data } = await axiosApiInstance.get<U>(url, { ...options, headers, cancelToken: cancelTokenSource?.token });
    return data;
  } catch (error) {
    handleRequestErrors(error as AxiosError);
    throw error;
  }
}

export async function apiPut<T, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data }: any = await axiosApiInstance.put(url, body, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPost<T, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    return await axiosApiInstance.post(url, body, { ...options, headers });
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiDelete<T, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data }: any = await axiosApiInstance.delete(url, { data: body, ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPostFile<T, V = any>(
  url: string,
  fileData: string | Blob | RcFile | FormData,
  options?: T,
  destructuringData?: boolean
): Promise<V> {
  try {
    const isFormData = fileData instanceof FormData;

    const token = await getToken();
    var formData = new FormData();
    if (!isFormData) formData.append('file', fileData);

    const file = isFormData ? fileData : formData;

    const headers = {
      'Content-Type': 'multipart/form-data',
      Authorization: token
    };
    if (destructuringData) {
      const { data } = await axiosApiInstance.post(url, file, { ...options, headers });
      return data;
    }
    return await axiosApiInstance.post(url, file, { ...options, headers });
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPatch<T = any, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  const token = await getToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: token
  };
  try {
    const { data }: any = await axiosApiInstance.patch(url, body, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPutFile<T, U = any, V = any>(url: string, body?: FormData, options?: T): Promise<V> {
  const token = await getToken();
  const headers = {
    'Content-Type': 'multipart/form-data',
    Authorization: token
  };
  try {
    const { data }: any = await axiosApiInstance.put(url, body, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export interface ISseStreamSubscriptionCallbacks {
  onOpen: () => void;
  onMessageData: (data: string) => void;
  onFailure: () => void;
}

/**
 * Gestiona una conexión SSE con autenticación.
 * Retorna una función de limpieza para abortar la conexión.
 */
export const connectSseStream = (streamUrl: string, callbacks: ISseStreamSubscriptionCallbacks): (() => void) => {
  const controller = new AbortController();
  let failureNotified = false;

  // Evita notificar error si ya se cerró o ya se avisó una vez
  const notifyFailure = (): void => {
    if (controller.signal.aborted || failureNotified) return;
    failureNotified = true;
    callbacks.onFailure();
  };

  // Fuerza el fin de reintentos en la librería lanzando un error
  const raiseToStopRetries = (err: unknown): never => {
    throw err instanceof Error ? err : new Error(String(err));
  };

  // Procesa errores externos y notifica al callback de falla
  const reportConnectionFailure = (err: unknown): void => {
    if (controller.signal.aborted || failureNotified) return;
    handleRequestErrors(err as AxiosError);
    notifyFailure();
  };

  const handleSseStream = async () => {
    try {
      // 1. Obtención de credenciales antes de conectar
      const token = await getToken();
      if (!token) return notifyFailure();

      // 2. Inicio del flujo con fetch-event-source
      await fetchEventSource(streamUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          Authorization: token,
          Accept: 'text/event-stream'
        },
        openWhenHidden: true,
        onopen: async response => {
          if (controller.signal.aborted) raiseToStopRetries('aborted');

          // Validaciones de status y tipo de contenido
          if (!response.ok) {
            console.error('SSE response error', response);
            raiseToStopRetries(`HTTP ${response.status}`);
          }

          if (!response.headers.get('content-type')?.includes('text/event-stream')) {
            raiseToStopRetries('Invalid Content-Type');
          }

          if (!controller.signal.aborted) callbacks.onOpen();
        },
        onmessage: ev => {
          // 3. Emisión de datos si la conexión sigue viva
          if (!controller.signal.aborted && ev.data) {
            callbacks.onMessageData(ev.data);
          }
        },
        onerror: err => {
          // 4. Gestión de errores durante el streaming
          if (controller.signal.aborted) raiseToStopRetries(err);
          reportConnectionFailure(err);
          raiseToStopRetries(err);
        }
      });
    } catch (err) {
      // 5. Catch final para errores de red o lógica inicial
      if (!controller.signal.aborted) reportConnectionFailure(err);
    }
  };

  handleSseStream();

  // Función de limpieza (unsubscribe)
  return () => controller.abort();
};
